import { useState } from 'react';
import Required from '../../../common-components/Required';
import CloseButton from '../../../custom-components/CloseButton';
import CancelButton from '../../../custom-components/CancelButton';
import BlackButton from '../../../custom-components/BlackButton';
import FileUploader from '../../../common-components/FileUploader';
import { useDispatch, useSelector } from 'react-redux';
import {
  fileDelete,
  fileUpload,
  multipleFileUpload,
  previewImage,
  progressPercentage,
  resetMediaErrorAlert,
  resetMultipleFileUpload,
  setSelectedFile,
} from '../../../../slices/FileUploadSlice';
import { RefuelStringLabels } from '../../../../util/Strings';
import {
  setPreThumbnailId,
  setPrevRefuelIds,
  setPrevRefuelImageDetails,
  setPrevThumbnailDetails,
} from '../../../../slices/RefuelSlice';
import ImagePreviewWithFilename from '../../../common-components/ImagePreviewWithFilename';
import { NewRefuelFormValidations } from '../../../../util/Validations';
import ErrorMessageComponent from '../../../common-components/ErrorMessageComponent';
import useFileValidation from '../../../../util/useFileValidation';
import Select from 'react-select';
import ErrorAlert from '../../../common-components/ErrorAlert';
import ErrorMessages from '../../../../util/ErrorMessages';

function AddNewRefuelForm({ show, onClickNext, data }) {
  const dispatch = useDispatch();

  // Normalize initial arrays to [] to simplify logic
  const initialRefuelPhotoIds = data?.refuelPhotoIds ?? [];
  const multipleFileNameWithImageUrls = [];
  if (data && data.photoUrls && data.photoFileNames) {
    for (let i = 0; i < data.photoUrls.length; i++) {
      multipleFileNameWithImageUrls.push({
        photoUrl: data.photoUrls[i],
        fileName: data.photoFileNames[i],
        id: data.refuelPhotoIds[i],
      });
    }
  }

  const [state, setState] = useState({
    name: data?.name ?? null,
    type: data?.type ?? null,
    price: data?.price ?? null,
    address: data?.address ?? null,
    description: data?.description ?? null,
    latitude: data?.latitude ?? null,
    longitude: data?.longitude ?? null,
    refuelThumbnailId: data?.refuelThumbnailId ?? null,
    refuelPhotoIds: initialRefuelPhotoIds,
  });

  const [prevFileData, setPreFileData] = useState({
    photoUrl: data?.thumbnailUrl ?? null,
    fileName: data?.thumbnailFileName ?? null,
  });

  const [prevFileId, setPrevFileId] = useState(data?.refuelThumbnailId ?? null);
  const [prevMultipleFileIds, setPrevMultipleFileIds] = useState(initialRefuelPhotoIds);
  const [prevMultiFileData, setPrevMultiFileData] = useState(multipleFileNameWithImageUrls ?? []);
  const [imageDeleteList, setImageDeleteList] = useState([]);
  const [validation, setValidation] = useState({});

  const prevThumbnailId = useSelector((s) => s.refuel.prevThumbnailId);
  const prevRefuelIds = useSelector((s) => s.refuel.prevRefuelIds);
  const localCurrency = useSelector((s) => s.refuel.localCurrency);
  const errorMessageCode = useSelector((s) => s.upload.errorCode);
  const prevRefuelThumbnailFileData = useSelector((s) => s.refuel.prevThumbnailDetails);
  const prevRefuelMultipleFileData = useSelector((s) => s.refuel.prevRefuelImageDetails);

  const {
    errorSingleMessage,
    setErrorSingleMessage,
    errorMultiMessage,
    setErrorMultiMessage,
    validateFile,
    validateFiles,
  } = useFileValidation();

  const refuelTypeOptions = [
    { value: 1, label: 'North Indian' },
    { value: 2, label: 'Continental' },
    { value: 3, label: 'Asian' },
    { value: 4, label: 'South Indian' },
    { value: 5, label: 'Multi-Cuisine' },
    { value: 6, label: 'Mexican' },
    { value: 7, label: 'French' },
    { value: 8, label: 'Italian' },
    { value: 9, label: 'International' },
    { value: 10, label: 'Argentinian' },
    { value: 11, label: 'Brazilian' },
    { value: 12, label: 'Mediterranian' },
  ];

  function handleCloseErrorAlert() {
    dispatch(resetMediaErrorAlert(false));
    setPrevMultiFileData(prevRefuelMultipleFileData ?? multipleFileNameWithImageUrls);
    handleMultipleImageState(); // will sync state with prevMultipleFileIds
    dispatch(setSelectedFile(null));
    setPreFileData(prevRefuelThumbnailFileData ?? { photoUrl: data?.thumbnailUrl ?? null, fileName: data?.thumbnailFileName ?? null });
    handleState();
    dispatch(setSelectedFile(null));
    setImageDeleteList([]);
    onClickNext();
  }

  const checkValidation = () => {
    const { errors, isValid } = NewRefuelFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  function handleChange(e) {
    const value = e.target.value;
    setState((prev) => ({
      ...prev,
      [e.target.name]: value,
    }));
  }

  function resetImage() {
    dispatch(progressPercentage(0));
    dispatch(setSelectedFile(null));
    dispatch(previewImage(null));
    dispatch(resetMultipleFileUpload());
  }

  // ===== Safe handlers =====
  function handleImageCancel(photoId) {
    // remove preview object safely
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((image) => image.id !== photoId) : prev));

    // remove id from prevMultipleFileIds and update form state
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== photoId) : [];
      setState((prevState) => ({ ...prevState, refuelPhotoIds: ids }));
      return ids;
    });

    // add to delete list using functional updater to avoid stale closure issues
    setImageDeleteList((prev) => [...prev, photoId]);
  }

  function handleThumbnailCancel() {
    // clear thumbnail id in form state
    setState((prev) => ({ ...prev, refuelThumbnailId: null }));

    // avoid mutating prevFileData directly
    setPreFileData({ photoUrl: null, fileName: null });

    // mark prevFileId for deletion if exists, and clear it
    setPrevFileId((currentPrevFileId) => {
      if (currentPrevFileId) {
        setImageDeleteList((prev) => [...prev, currentPrevFileId]);
      }
      return null;
    });
  }

  // Here is the key async change: awaiting deletes and onClickNext
  async function handleNextButton() {
    if (checkValidation()) {
      if (
        state.name &&
        state.type &&
        state.price &&
        state.address &&
        state.latitude &&
        state.longitude &&
        state.description
      ) {
        const newData = state;

        if (data) {
          setState({
            name: newData.name,
            description: newData.description,
            latitude: newData.latitude,
            longitude: newData.longitude,
            address: newData.address,
            type: newData.type,
            price: newData.price,
            photo: newData.photo,
            refuelThumbnailId: newData.refuelThumbnailId,
            refuelPhotoIds: newData.refuelPhotoIds,
          });

          // dispatch deletes explicitly (use forEach instead of map for side-effects)
          if (imageDeleteList && imageDeleteList.length) {
            await Promise.all(
              imageDeleteList.map((imageId) =>
                dispatch(fileDelete({ id: imageId, isVideo: false, isMulti: true })).unwrap()
              )
            );
          }

          resetImage();

          if (onClickNext) {
            await onClickNext(newData);
          }

          if (state.refuelThumbnailId !== prevThumbnailId && prevThumbnailId !== '') {
            const id = { id: prevThumbnailId, isVideo: false };
            await dispatch(fileDelete(id)).unwrap();
            dispatch(setPreThumbnailId(''));
          }

          setPreFileData({
            photoUrl: data?.thumbnailUrl ?? null,
            fileName: data?.thumbnailFileName ?? null,
          });

          setImageDeleteList([]);
          setErrorSingleMessage('');
          setErrorMultiMessage('');
        } else {
          setState({});
          resetImage();
          setImageDeleteList([]);
          if (onClickNext) {
            await onClickNext(newData);
          }
        }
      } else {
        if (onClickNext) {
          await onClickNext();
        }
      }
    }
  }

  function handleMultipleImageState(fileId) {
    // if no fileId provided, sync state to prevMultipleFileIds
    if (fileId === undefined) {
      setState((prevState) => ({ ...prevState, refuelPhotoIds: prevMultipleFileIds }));
      return;
    }
    setPrevMultipleFileIds((prevIds) => {
      const ids = Array.isArray(prevIds) ? prevIds.filter((id) => id !== fileId) : [];
      setState((prevState) => ({ ...prevState, refuelPhotoIds: ids }));
      return ids;
    });
    setPrevMultiFileData((prev) => (Array.isArray(prev) ? prev.filter((img) => img.id !== fileId) : prev));
  }

  const handleFileSelect = (event) => {
    const files = event.target.files;
    postFileData(files);
  };

  const handleMultiFileSelect = (event) => {
    const files = event.target.files;
    postMultipleFileData(files);
  };

  const handleDrag = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  const handleMultiDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postMultipleFileData(files);
  };

  function handleState() {
    setState((prev) => ({ ...prev, refuelThumbnailId: data?.refuelThumbnailId ?? null }));
  }

  function postMultipleFileData(files) {
    const valid = validateFiles(files);
    if (valid) {
      if (prevMultiFileData && prevMultiFileData.length) {
        dispatch(setPrevRefuelIds(prevMultipleFileIds));
        dispatch(setPrevRefuelImageDetails(prevMultiFileData));
      }

      dispatch(multipleFileUpload(files))
        .unwrap()
        .then((res) => {
          const newIds = res.map((r) => r._id);
          setPrevMultipleFileIds((prevIds) => {
            const ids = Array.isArray(prevIds) ? prevIds.concat(newIds) : newIds;
            setState((prevState) => ({ ...prevState, refuelPhotoIds: ids }));
            return ids;
          });
        })
        .catch((err) => {
          console.debug('multipleFileUpload failed', err);
        });
    }
  }

  function postFileData(files) {
    const valid = validateFile(files);
    if (valid) {
      const file = files[0];
      if (prevFileData && (prevFileData.photoUrl || prevFileData.fileName)) {
        dispatch(setPreThumbnailId(prevFileId));
        setPrevFileId((curr) => curr); // keep same prevFileId as reference
        dispatch(setPrevThumbnailDetails(prevFileData));
        setPreFileData({ photoUrl: null, fileName: null });
      }
      if (file) {
        dispatch(setSelectedFile(file));
        dispatch(fileUpload(file))
          .unwrap()
          .then((res) => {
            setState((prevState) => ({ ...prevState, refuelThumbnailId: res[0]._id }));
            setPrevFileId(res[0]._id);
          })
          .catch((err) => {
            console.debug('fileUpload failed', err);
          });
      }
    }
  }

  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  if (!show) {
    return null;
  }

  return (
    <div className="modal">
      <div className="form-scroll">
        <div className="form-flex">
          <div className="user-form-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <div className="header">
                {data ? RefuelStringLabels.formTitleUpdate : RefuelStringLabels.formTitleAdd}
              </div>
              <CloseButton
                onClickhandle={() => {
                  onClickNext();
                  if (data === null) {
                    setState({});
                    setValidation({});
                  }
                  setPreFileData({
                    photoUrl: data?.thumbnailUrl ?? null,
                    fileName: data?.thumbnailFileName ?? null,
                  });
                  setPrevMultipleFileIds(data?.refuelPhotoIds ?? []);
                  setPrevMultiFileData(multipleFileNameWithImageUrls);
                  setImageDeleteList([]);
                  setErrorSingleMessage('');
                  setErrorMultiMessage('');
                  resetImage();
                }}
              />
            </div>

            <div className="form-container">
              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.name}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    name="name"
                    value={state.name ?? ''}
                    onChange={(e) => handleChange(e)}
                    className="form-input"
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.name} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.description}
                  <Required />
                </label>
                <div className="user-form-input-container">
                  <textarea
                    className="form-input"
                    type="text"
                    name="description"
                    value={state.description ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                    rows={3}
                  />
                </div>
                <ErrorMessageComponent message={validation.description} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.address}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="address"
                    value={state.address ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.address} />
              </div>

              <div className="form-label-input-select">
                <label className="form-label">
                  {RefuelStringLabels.type}
                  <Required />
                </label>
                <Select
                  options={refuelTypeOptions}
                  className="basic-single"
                  classNamePrefix="select"
                  name="eventType"
                  defaultValue={data ? refuelTypeOptions.filter((event) => event.label === data.type) : undefined}
                  closeMenuOnSelect={true}
                  onChange={(e) => {
                    setState((prev) => ({ ...prev, type: e.label }));
                  }}
                />
                <ErrorMessageComponent message={validation.type} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.price}(in {localCurrency})
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="price"
                    value={state.price ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="Input"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.price} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.latitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="latitude"
                    value={state.latitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 12.9845"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.latitude} />
              </div>

              <div className="form-label-input">
                <label className="form-label">
                  {RefuelStringLabels.longitude}
                  <Required />
                </label>
                <div className="form-input-container">
                  <input
                    className="form-input"
                    type="text"
                    name="longitude"
                    value={state.longitude ?? ''}
                    onChange={(e) => handleChange(e)}
                    placeholder="e.g. 77.5846"
                    required={true}
                  />
                </div>
                <ErrorMessageComponent message={validation.longitude} />
              </div>

              <FileUploader
                previewShape="rectangle"
                headerName={RefuelStringLabels.thumbnail}
                handleDragOver={handleDrag}
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                setPreFileData={setPreFileData}
                handleState={handleState}
                type="refuel"
              />
              {errorSingleMessage !== '' && <p className="error image-error">{errorSingleMessage}</p>}
              {prevFileData?.photoUrl && data?.thumbnailUrl && data?.thumbnailFileName ? (
                <ImagePreviewWithFilename
                  photoUrl={data.thumbnailUrl}
                  fileName={data.thumbnailFileName}
                  handleCancel={handleThumbnailCancel}
                />
              ) : null}

              <FileUploader
                previewShape="rectangle"
                headerName={RefuelStringLabels.photos}
                handleDragOver={handleDrag}
                handleDrop={handleMultiDrop}
                handleFileSelect={handleMultiFileSelect}
                isMultiUpload={true}
                setMultipleFileData={setPrevMultiFileData}
                handleMultiState={handleMultipleImageState}
                type="refuel"
              />
              {errorMultiMessage !== '' && <p className="error image-error">{errorMultiMessage}</p>}
              {Array.isArray(prevMultiFileData) &&
                prevMultiFileData.length >= 1 &&
                prevMultiFileData.map((url) => {
                  return (
                  <ImagePreviewWithFilename
                    key={url.id}
                    photoUrl={url.photoUrl}
                    fileName={url.fileName}
                    photoId={url.id}
                    handleCancel={handleImageCancel}
                  />
                  );
                })}

              <div className="footer-button-container">
                <CancelButton
                  onClickhandle={() => {
                    onClickNext();
                    if (data === null) {
                      setState({});
                    }
                    setValidation({});
                    setPreFileData({
                      photoUrl: data?.thumbnailUrl ?? null,
                      fileName: data?.thumbnailFileName ?? null,
                    });
                    setPrevMultipleFileIds(data?.refuelPhotoIds ?? []);
                    setPrevMultiFileData(multipleFileNameWithImageUrls);
                    setImageDeleteList([]);
                    setErrorSingleMessage('');
                    setErrorMultiMessage('');
                    resetImage();
                  }}
                >
                  {RefuelStringLabels.cancel}
                </CancelButton>
                <BlackButton onClickhandle={() => handleNextButton()}>
                  {data ? RefuelStringLabels.update : RefuelStringLabels.add}
                </BlackButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNewRefuelForm;